import { Fragment, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MarkdownText } from 'components/MarkdownText'
import { SourceRow } from 'components/messages/SourceRow'
import { AgentActionCard } from 'components/messages/AgentActionCard'
import { ViewResultLink } from 'components/messages/ViewResultLink'
import { ToolFileCard } from 'components/messages/ToolFileCard'
import { MessageSection } from 'components/messages/MessageSection'
import { CitationSource } from 'types/chat-message'
import { processCitationMarkers, parseAgentActions } from 'utils/citations'

type MessageContentProps = {
  /** Raw bot message text (may include <<<SOURCES>>>, <<<ACTION_REQUIRED>>> and citation markers). */
  text: string
}

/**
 * Shared bot message body: markdown + inline citations + sources block, plus the
 * agent action (HITL) card when the bot paused on a write action. Chrome (bubble,
 * avatar, feedback) is left to the caller.
 */
const MessageContentComponent = ({ text }: MessageContentProps) => {
  const { t } = useTranslation()

  // Split the agent actions off first: one card per pause, in order, the conversational text
  // rendering around them. The sources of every resumed stream are merged by the parser.
  const { segments, after, sources, views } = useMemo(() => parseAgentActions(text), [text])
  const parsedSegments = useMemo(
    () => segments.map((segment) => ({ action: segment.action, text: processCitationMarkers(segment.text) })),
    [segments],
  )
  const processedText = useMemo(() => processCitationMarkers(after), [after])

  const textSources = useMemo(() => {
    if (!sources) return [] as [string, CitationSource][]
    return Object.entries(sources).filter(([, s]) => !s.imageUrl)
  }, [sources])

  const remoteSources = useMemo(
    () => textSources.filter(([, s]) => s.type !== 'attachment' && s.type !== 'tool'),
    [textSources],
  )
  const attachmentSources = useMemo(() => textSources.filter(([, s]) => s.type === 'attachment'), [textSources])
  const toolSources = useMemo(() => textSources.filter(([, s]) => s.type === 'tool'), [textSources])
  // Tool outputs that are a document to open (charts are drawn inline instead): shown up front, not collapsed.
  const fileSources = useMemo(() => toolSources.filter(([, s]) => s.url && s.kind !== 'chart'), [toolSources])

  // Group labels are only worth showing when the section mixes more than one group.
  const groupCount = [remoteSources, attachmentSources].filter((g) => g.length > 0).length

  const renderGroup = (label: string, group: [string, CitationSource][]) =>
    group.length > 0 && (
      <div className="flex flex-col gap-0.5">
        {groupCount > 1 && <p className="m-0 text-xs font-semibold text-muted-foreground">{label}</p>}
        {group.map(([key, source]) => (
          <SourceRow key={key} sourceKey={key} source={source} />
        ))}
      </div>
    )

  const sourcesCount = remoteSources.length + attachmentSources.length
  const sourcesBlock = sourcesCount > 0 && (
    <MessageSection icon={['far', 'book-open']} title={t('sourcesSectionTitle')} count={sourcesCount}>
      <div className="flex flex-col gap-2">
        {renderGroup(remoteSources.length === 1 ? t('sourcesSingular') : t('sourcesPlural'), remoteSources)}
        {renderGroup(t('uploadedDocsTitle'), attachmentSources)}
      </div>
    </MessageSection>
  )

  const filesBlock = fileSources.length > 0 && (
    <div className="mt-3 flex flex-col gap-2">
      {fileSources.map(([key, source]) => (
        <ToolFileCard key={key} source={source} />
      ))}
    </div>
  )

  const toolsBlock = toolSources.length > 0 && (
    <MessageSection icon={['far', 'gear']} title={t('toolsUsedTitle')} count={toolSources.length}>
      <div className="flex flex-col gap-0.5">
        {toolSources.map(([key, source]) => (
          <SourceRow key={key} sourceKey={key} source={source} />
        ))}
      </div>
    </MessageSection>
  )

  const viewsBlock = views && views.length > 0 && (
    <MessageSection icon={['far', 'arrow-up-right']} title={t('viewsSectionTitle')} count={views.length}>
      <div className="flex flex-col gap-0.5">
        {views.map((view, i) => (
          <ViewResultLink key={`${view.routeName}-${i}`} view={view} />
        ))}
      </div>
    </MessageSection>
  )

  if (parsedSegments.length > 0) {
    return (
      <>
        {/* Only the last card can be pending. Resolved means the bot continued PAST the action,
            so test the parsed continuation and not the raw tail: that also carries the
            <<<SOURCES>>>/<<<VIEWS>>> control blocks emitted along with the pause itself. */}
        {parsedSegments.map((segment, index) => (
          <Fragment key={`${segment.action.runId}-${index}`}>
            {segment.text && <MarkdownText text={segment.text} sources={sources} />}
            <AgentActionCard action={segment.action} resolved={index < parsedSegments.length - 1 || !!processedText} />
          </Fragment>
        ))}
        {processedText && <MarkdownText text={processedText} sources={sources} />}
        {filesBlock}
        {sourcesBlock}
        {toolsBlock}
        {viewsBlock}
      </>
    )
  }

  return (
    <>
      <MarkdownText text={processedText} sources={sources} />
      {filesBlock}
      {sourcesBlock}
      {toolsBlock}
      {viewsBlock}
    </>
  )
}

export const MessageContent = memo(MessageContentComponent)
